import { Component, inject, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  FormGroup,
  FormsModule,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  MOCK_OFFICE_CODE_OPTIONS,
  MOCK_CLAIM_WORDING_OPTIONS,
} from '../../mocks/form-options-mock-data';
import { OFFICE_CODES } from '../../constants/office-codes.constants';
import {
  ADD_EDIT_DIALOG_CONSTANTS,
  CLAIMS_MICRO_UI,
  CLASS_NAME,
  UI_CONSTANTS,
} from '../../constants/claims-ui-constants';
import { User } from '../../interfaces/user.model';
import { IClaim, ICreateClaimRequest } from '../../interfaces/claims.model';
import { SearchOption } from '../../interfaces/searchoption.model';
import { ClaimsService } from '../../../core/services/claims.service';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import {
  GenericSearchComponent,
  SearchSelection,
} from '../generic-search/generic-search.component';
import { IOffice } from '../../interfaces/office.interface';
import type { IClaimsDialogData } from '../../interfaces/claims-dialog.model';
import { GridService } from '../../../core/services/grid.service';
import { SearchOptionService } from '../../services/search-option.service';
import { DialogDataMapperService } from '../../services/dialog-data-mapper.service';
import { TabOrderService } from '../../services/tab-order.service';
import { ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';


@Component({
  selector: 'emma-add-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    GenericSearchComponent,
    CdkDrag,
    CdkDragHandle,
    CdkScrollable,
  ],
  templateUrl: './add-edit-dialog.component.html',
  styleUrls: ['./add-edit-dialog.component.scss'],
})
export class AddEditDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  public isMinimizeState = true;
  public form: FormGroup;
  public officeCodeOptions = MOCK_OFFICE_CODE_OPTIONS;
  public claimWordingOptions = MOCK_CLAIM_WORDING_OPTIONS;
  public officeOptions: Array<{ name: string; code: string; country: string; id: number }> = [];
  public selectedOffice: IOffice | null = null;
  public userOptions: Array<User> = [];
  public filteredClaimWordingOptions: Array<{ id: string; text: string; category: string }> = [];
  private currentResearchUserSearchTerm = '';
  private currentOpportunityOwnerSearchTerm = '';
  private currentClaimRenewalSearchTerm = '';
  public readonly placeholders = UI_CONSTANTS.PLACEHOLDERS;
  public officeCodeSearchOptions: Array<SearchOption> = [];
  public researchUserSearchOptions: Array<SearchOption> = [];
  public opportunityOwnerSearchOptions: Array<SearchOption> = [];
  public claimRenewalSearchOptions: Array<SearchOption> = [];
  public languageOptions: Array<SearchOption> = [];
  public languageSearchOptions: Array<SearchOption> = [];
  public isLoadingClaimRenewal = false;
  public isLoadingLanguages = false;
  public isSubmitting = false;
  public submitError: string | null = null;
  private allClaims: Array<IClaim> = [];
  private initialFormValue: any = null;
  private readonly destroy$ = new Subject<void>();
  private readonly researchUserSearch$ = new Subject<string>();
  private readonly opportunityOwnerSearch$ = new Subject<string>();
  public crmAccountMappingMessage: string | null = null;
  private readonly gridService = inject(GridService);
  private readonly searchOptionService = inject(SearchOptionService);
  private readonly dataMapperService = inject(DialogDataMapperService);
  private readonly tabOrderService = inject(TabOrderService);
  @ViewChild('expiryDateInput') public expiryDateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('expiryDatePicker') public expiryDatePicker!: MatDatepicker<Date>;
  @ViewChild('counterSignDateInput') public counterSignDateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('counterSignDatePicker') public counterSignDatePicker!: MatDatepicker<Date>;
  @ViewChild('officeCodeDisplay', { read: ElementRef }) public officeCodeDisplayRef!: ElementRef;
  @ViewChild('claimRenewalId', { read: ElementRef }) public claimRenewalIdRef!: ElementRef;


  constructor(
    public dialogRef: MatDialogRef<AddEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IClaimsDialogData,
    private readonly fb: FormBuilder,
    private readonly claimsService: ClaimsService,
    private readonly snackBarService: SnackBarService,
    private readonly elRef: ElementRef
  ) {
    this.form = this.createFormGroup();
    this.patchFormDataIfEdit();
  }

  private setFormControlsDisabledState(): void {
    const controlsToDisable = [
      'client',
      'sfAccountId',
      'opportunityOwnerEmail',
      'claimRenewalId',
      'mainResearchUserId',
      'expiryDate',
      'claimWording',
      'claimFootnote',
      'counterSignDate',
      'crmLink',
      'languageTranslation',
      'officeCodeDisplay',
    ];
    for (const controlName of controlsToDisable) {
      const control = this.form.get(controlName);
      if (control) {
        if (this.isViewMode) {
          control.disable();
        } else {
          control.enable();
        }
      }
    }
  }

  public ngOnInit(): void {
    this.initializeSearchOptions();
    this.setFormControlsDisabledState();
    this.setupCrmAccountListener();
    this.setupDebouncedSearch();
    this.loadLanguageOptions();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.tabOrderService.detachKeydownListener();
  }

  public ngAfterViewInit(): void {
    this.attachTabListener();
    this.addKeyboardAccessibility();
  }

  public addKeyboardAccessibility(): void {
    if (this.officeCodeDisplayRef?.nativeElement) {
      this.officeCodeDisplayRef.nativeElement.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          const dropdown = this.officeCodeDisplayRef.nativeElement.closest('emma-generic-search')?.querySelector('.dropdown-display');
          if (dropdown) (dropdown as HTMLElement).click();
        }
      });
    }
    if (this.claimRenewalIdRef?.nativeElement) {
      this.claimRenewalIdRef.nativeElement.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          const dropdown = this.claimRenewalIdRef.nativeElement.closest('emma-generic-search')?.querySelector('.dropdown-display');
          if (dropdown) (dropdown as HTMLElement).click();
        }
      });
    }
    if (this.expiryDateInput && this.expiryDatePicker) {
      this.expiryDateInput.nativeElement.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          this.expiryDatePicker.open();
        }
      });
    }
    if (this.counterSignDateInput && this.counterSignDatePicker) {
      this.counterSignDateInput.nativeElement.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          this.counterSignDatePicker.open();
        }
      });
    }

  }

  private attachTabListener(): void {
    if (this.form) {
      this.tabOrderService.attachKeydownListener(this.elRef, this.form, "claims");
    }
  }

  private createFormGroup(): FormGroup {
    return this.fb.group({
      client: ['', [Validators.required, Validators.maxLength(250)]],
      opportunityOwnerEmail: ['', []],
      opportunityOwnerName: ['', []],
      claimRenewalId: ['', []],
      claimRenewalName: ['', []],
      mainResearchUserId: ['', []],
      mainResearchUserName: ['', []],
      counterSignDate: [null, [Validators.required]],
      sfAccountId: ['', [Validators.required, Validators.maxLength(250), Validators.pattern('^[a-zA-Z0-9]+$')]],
      officeCode: ['', []],
      officeCodeDisplay: ['', [Validators.required]],
      expiryDate: [null, [Validators.required]],
      claimWording: ['', [Validators.required, Validators.maxLength(500)]],
      claimFootnote: ['', [Validators.required, Validators.maxLength(500)]],
      crmLink: ['', [Validators.maxLength(250), Validators.pattern('^(https?://.+)?$')]],
      languageTranslation: [''],
    });
  }

  private patchFormDataIfEdit(): void {
    if (!this.data?.claimData) return;

    const patch = this.normalizePatchData(this.data.claimData);
    this.form.patchValue(patch);
    this.selectedOffice = this.dataMapperService.getOfficeFromCode(patch.officeCode);
    this.initialFormValue = this.form.getRawValue();
  }

  private normalizePatchData(claimData: any): any {
    const patch = { ...claimData };
    if (patch.expiryDate && typeof patch.expiryDate === 'string') {
      patch.expiryDate = new Date(patch.expiryDate);
    }
    if (patch.counterSignDate && typeof patch.counterSignDate === 'string') {
      patch.counterSignDate = new Date(patch.counterSignDate);
    }

    return patch;
  }

  private getFormControlValue(controlName: string): any {
    return this.form.get(controlName)?.value;
  }

  private setFormControlValue(controlName: string, value: any): void {
    this.form.get(controlName)?.setValue(value);
  }

  private initializeSearchOptions(): void {
    if (!this.data?.claimData) {
      this.clearAllSearchOptions();
      return;
    }

    this.initializeOfficeOptions();
    this.initializeOpportunityOwnerOptions();
    this.initializeResearchUserOptions();
    this.initializeClaimRenewalOptions();
    this.initializeLanguageOptions();
  }

  private setupCrmAccountListener(): void {
    this.form.get('sfAccountId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.crmAccountMappingMessage = null;
      });
  }

  public getCrmCompanyMapping(): void {
    const crmAccountId = this.form.get('sfAccountId')?.value;
    const control = this.form.get('sfAccountId');
    this.crmAccountMappingMessage = null;

    if (control?.errors || !crmAccountId || crmAccountId.trim().length === 0) {
      return;
    }

    if (crmAccountId.trim().length < 3) {
      this.crmAccountMappingMessage = CLAIMS_MICRO_UI.companyMappingNotFound;
      return;
    }

    this.claimsService.getCompanyNameByCrmAccountId(crmAccountId).subscribe({
      next: (companyName: string | null) => {
        if (companyName) {
          this.crmAccountMappingMessage = `${CLAIMS_MICRO_UI.companyMappingFound} ${companyName}`;
        } else {
          this.crmAccountMappingMessage = CLAIMS_MICRO_UI.companyMappingNotFound;
        }
      },
      error: () => {
        this.crmAccountMappingMessage = CLAIMS_MICRO_UI.companyMappingNotFound;
      }
    });
  }

  private initializeOfficeOptions(): void {
    const officeCode = this.data.claimData?.officeCode;
    if (officeCode) {
      const office = this.dataMapperService.getOfficeFromCode(officeCode);
      if (office) {
        this.officeCodeSearchOptions = [
          {
            id: office.offCode,
            name: office.offName,
            code: office.offCode,
          },
        ];
        this.setFormControlValue('officeCodeDisplay', office.offName);
        this.selectedOffice = office;
      }
    }
  }

  private initializeOpportunityOwnerOptions(): void {
    const data = this.data.claimData;
    const email = data?.opportunityOwnerEmail;
    const name = data?.opportunityOwnerName;

    if (email && name) {
      this.opportunityOwnerSearchOptions = [
        this.dataMapperService.createPrepopulatedOption(email, name, email),
      ];
      this.setFormControlValue('opportunityOwnerEmail', email);
      this.setFormControlValue('opportunityOwnerName', name);
    }
  }

  private initializeResearchUserOptions(): void {
    const data = this.data.claimData;
    const userId = data?.mainResearchUserId;
    const name = data?.mainResearchUserName;

    if (userId && name) {
      this.researchUserSearchOptions = [
        this.dataMapperService.createPrepopulatedOption(userId, name, String(userId)),
      ];
      this.setFormControlValue('mainResearchUserId', userId);
      this.setFormControlValue('mainResearchUserName', name);
    }
  }

  private initializeClaimRenewalOptions(): void {
    const data = this.data.claimData;
    const renewalId = data?.claimRenewalId;
    const renewalWording = data?.claimRenewalName;

    if (renewalId && renewalWording) {
      this.claimRenewalSearchOptions = [
        {
          id: renewalId,
          name: renewalWording,
          code: String(renewalId)
        }
      ];
      this.setFormControlValue('claimRenewalId', renewalId);
      this.loadAllClaims();
    }
  }

  private initializeLanguageOptions(): void {
    const data = this.data.claimData;
    const languageId = data?.languageTranslation;
    const languageName = data?.languageTranslationName || data?.languageTranslation;

    if (languageId && languageName) {
      this.languageOptions = [
        {
          id: languageId,
          name: languageName,
          code: String(languageId),
        }
      ];
      this.languageSearchOptions = [...this.languageOptions];
      this.setFormControlValue('languageTranslation', languageId);
    }
  }

  private clearAllSearchOptions(): void {
    this.officeCodeSearchOptions = [];
    this.researchUserSearchOptions = [];
    this.opportunityOwnerSearchOptions = [];
    this.claimRenewalSearchOptions = [];
    this.languageOptions = [];
    this.languageSearchOptions = [];
  }

  public onClaimRenewalSearch(searchTerm: string): void {
    this.currentClaimRenewalSearchTerm = searchTerm;
    if (searchTerm.length < 1) {
      this.displayAllClaimRenewalOptions();
    } else {
      this.performClaimRenewalSearch(searchTerm);
    }
  }

  public onClaimRenewalEnter(): void {
    if (this.claimRenewalSearchOptions.length === 0) {
      this.loadAllClaims();
    }
  }

  public onClaimRenewalClick(): void {
    if (this.allClaims.length > 0) {
      this.displayAllClaimRenewalOptions();
    } else {
      this.loadAllClaims();
    }
  }

  public onClaimRenewalSelect(selection: SearchSelection): void {
    if (selection) {
      this.setFormControlValue('claimRenewalId', selection.id);
      this.currentClaimRenewalSearchTerm = '';
    } else {
      this.setFormControlValue('claimRenewalId', '');
    }
  }

  private performClaimRenewalSearch(searchTerm: string): void {
    if (this.allClaims.length === 0) {
      this.loadAndFilterClaims(searchTerm);
    } else {
      this.filterAndDisplayClaims(searchTerm);
    }
  }

  public get isViewMode(): boolean {
    return this.data?.mode?.toUpperCase() === 'VIEW';
  }

  private displayAllClaimRenewalOptions(): void {
    if (this.allClaims.length > 0) {
      this.claimRenewalSearchOptions = this.searchOptionService.mapClaimsToSearchOptions(
        this.allClaims,
      );
    }
  }

  private filterAndDisplayClaims(searchTerm: string): void {
    const filtered = this.searchOptionService.filterClaims(this.allClaims, searchTerm);
    this.claimRenewalSearchOptions =
      filtered.length > 0
        ? this.searchOptionService.mapClaimsToSearchOptions(filtered)
        : [this.searchOptionService.createNoResultsOption(searchTerm)];
  }

  private loadAndFilterClaims(searchTerm: string): void {
    this.loadClaimsFromService(() => this.filterAndDisplayClaims(searchTerm));
  }

  private loadAllClaims(): void {
    this.loadClaimsFromService(() => this.handleClaimsLoaded());
  }

  private loadClaimsFromService(onSuccess?: () => void): void {
    this.isLoadingClaimRenewal = true;
    const request = this.gridService.buildClaimsRequest(true);

    this.claimsService
      .getClaims(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (claims: Array<IClaim>) => {
          this.allClaims = claims || [];
          this.isLoadingClaimRenewal = false;
          onSuccess?.();
        },
        error: () => {
          this.isLoadingClaimRenewal = false;
          this.claimRenewalSearchOptions = [
            this.searchOptionService.createErrorOption(CLAIMS_MICRO_UI.claimsLoadingError),
          ];
        },
      });
  }

  private handleClaimsLoaded(): void {
    if (this.allClaims.length === 0) {
      this.claimRenewalSearchOptions = [this.searchOptionService.createNoResultsOption()];
      return;
    }

    this.claimRenewalSearchOptions = this.searchOptionService.mapClaimsToSearchOptions(
      this.allClaims,
    );
    this.revalidateSelectedClaim();

    if (this.currentClaimRenewalSearchTerm) {
      this.filterAndDisplayClaims(this.currentClaimRenewalSearchTerm);
    }
  }

  private revalidateSelectedClaim(): void {
    const currentValue = this.getFormControlValue('claimRenewalId');
    if (currentValue) {
      this.setFormControlValue('claimRenewalId', currentValue);
    }
  }

  public onIOfficeSearch(searchTerm: string): void {
    if (searchTerm.length < 1) {
      this.officeCodeSearchOptions = [];
      return;
    }

    const lower = searchTerm.toLowerCase();
    this.officeCodeSearchOptions = OFFICE_CODES.filter(
      (office) =>
        office.offName.toLowerCase().includes(lower) ||
        office.offCode.toLowerCase().includes(lower),
    ).map((office) => ({ id: office.offCode, name: office.offName, code: office.offCode }));
  }

  public onIOfficeSelect(selection: SearchSelection): void {
    if (!selection) {
      this.setFormControlValue('officeCode', '');
      this.setFormControlValue('officeCodeDisplay', '');
      this.selectedOffice = null;
      return;
    }
    const office = this.dataMapperService.getOfficeFromCode(String(selection.code || selection.id));
    if (office) {
      this.setFormControlValue('officeCode', office.offCode);
      this.setFormControlValue('officeCodeDisplay', office.offName);
      this.selectedOffice = office;
      this.officeCodeSearchOptions = [selection];
    }
  }

  public onIOfficeClick(): void {
    this.officeCodeSearchOptions = OFFICE_CODES.map((office) => ({
      id: office.offCode,
      name: office.offName,
      code: office.offCode,
    }));
  }

  private setupDebouncedSearch(): void {
    this.researchUserSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term) => {
        if (term.trim().length >= 3) {
          this.searchUsers(term, 'research');
        } else {
          this.researchUserSearchOptions = [];
        }
      });

    this.opportunityOwnerSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term) => {
        if (term.trim().length >= 3) {
          this.searchUsers(term, 'owner');
        } else {
          this.opportunityOwnerSearchOptions = [];
        }
      });
  }

  public onResearchUserSearch(searchTerm: string): void {
    this.currentResearchUserSearchTerm = searchTerm;
    this.researchUserSearch$.next(searchTerm);
  }

  public onResearchUserEnter(): void {
    if (this.currentResearchUserSearchTerm.length < 3) return;
    this.searchUsers(this.currentResearchUserSearchTerm, 'research');
  }

  public onResearchUserSelect(selection: SearchSelection): void {
    if (!selection) {
      this.setFormControlValue('mainResearchUserId', '');
      return;
    }
    this.setFormControlValue('mainResearchUserId', selection.id);
    this.setFormControlValue('mainResearchUserName', selection.name);
    this.researchUserSearchOptions = [selection];
    this.currentResearchUserSearchTerm = '';
  }

  public onOpportunityOwnerSearch(searchTerm: string): void {
    this.currentOpportunityOwnerSearchTerm = searchTerm;
    this.opportunityOwnerSearch$.next(searchTerm);
  }

  public onOpportunityOwnerEnter(): void {
    if (this.currentOpportunityOwnerSearchTerm.length < 3) return;
    this.searchUsers(this.currentOpportunityOwnerSearchTerm, 'owner');
  }

  public onOpportunityOwnerSelect(selection: SearchSelection): void {
    if (!selection) {
      this.setFormControlValue('opportunityOwnerEmail', '');
      this.setFormControlValue('opportunityOwnerName', '');
      return;
    }
    this.setFormControlValue('opportunityOwnerEmail', selection.code);
    this.setFormControlValue('opportunityOwnerName', selection.name);
    this.opportunityOwnerSearchOptions = [selection];
    this.currentOpportunityOwnerSearchTerm = '';
  }

  private searchUsers(searchTerm: string, fieldType: 'research' | 'owner'): void {
    this.claimsService
      .getUsers(searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users: Array<User>) => this.handleUsersLoaded(users, fieldType),
        error: () => this.handleUsersLoadError(fieldType),
      });
  }

  private handleUsersLoaded(users: Array<User>, fieldType: 'research' | 'owner'): void {
    if (!users || users.length === 0) {
      this.setUserSearchOptions(fieldType, [this.searchOptionService.createNoResultsOption()]);
      return;
    }

    const options = users.map((user) => ({
      id: fieldType === 'owner' ? user.userPrincipalName : user.id,
      name: user.fullName,
      code: fieldType === 'owner' ? user.userPrincipalName : String(user.id),
    }));

    this.setUserSearchOptions(fieldType, options);
  }

  private handleUsersLoadError(fieldType: 'research' | 'owner'): void {
    const error = [this.searchOptionService.createErrorOption(CLAIMS_MICRO_UI.claimsLoadingError)];
    this.setUserSearchOptions(fieldType, error);
  }

  private setUserSearchOptions(
    fieldType: 'research' | 'owner',
    options: Array<SearchOption>,
  ): void {
    if (fieldType === 'research') {
      this.researchUserSearchOptions = options;
    } else {
      this.opportunityOwnerSearchOptions = options;
    }
  }

  public searchIOffice(query: string): void {
    this.officeOptions =
      query.length < 1
        ? []
        : this.officeCodeOptions
            .filter(
              (o) =>
                o.code.toLowerCase().includes(query.toLowerCase()) ||
                o.label.toLowerCase().includes(query.toLowerCase()),
            )
            .map((o) => ({ id: 0, name: o.label, code: o.code, country: '' }));
  }

  public selectIOffice(officeCode: { code: string; label: string }): void {
    this.setFormControlValue('officeCode', officeCode.code);
    this.officeOptions = [];
  }

  public searchClaimWording(query: string): void {
    this.filteredClaimWordingOptions =
      query.length < 2
        ? []
        : this.claimWordingOptions.filter(
            (o) =>
              o.text.toLowerCase().includes(query.toLowerCase()) ||
              o.category.toLowerCase().includes(query.toLowerCase()),
          );
  }

  public selectClaimWording(claimWording: { id: string; text: string; category: string }): void {
    this.setFormControlValue('claimWording', claimWording.text);
    this.filteredClaimWordingOptions = [];
  }

  public getInputValue(event: Event): string {
    return (event.target && (event.target as HTMLInputElement).value) || '';
  }

  public get isFormValid(): boolean {
    return this.form.valid;
  }

  public get hasFormChanged(): boolean {
    if (!this.initialFormValue) {
      return this.form.dirty;
    }
    const currentValue = this.form.getRawValue();
    return JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);
  }

  public get dialogTitle(): string {
    const mode = (
      this.data?.mode ?? ''
    ).toUpperCase() as keyof typeof ADD_EDIT_DIALOG_CONSTANTS.DIALOG_TITLES;
    return ADD_EDIT_DIALOG_CONSTANTS.DIALOG_TITLES[mode] ?? '';
  }

  public get submitButtonText(): string {
    const isAdd = this.data?.mode === ADD_EDIT_DIALOG_CONSTANTS.MODES.ADD;
    return isAdd
      ? ADD_EDIT_DIALOG_CONSTANTS.BUTTON_TEXTS.ADD
      : ADD_EDIT_DIALOG_CONSTANTS.BUTTON_TEXTS.UPDATE;
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public restoreState(): void {
    this.isMinimizeState = !this.isMinimizeState;
  }

  public onSubmit(): void {
    if (!this.isFormValid || this.isSubmitting) {
      return;
    }
    const formData = this.prepareFormData();
    const isAdd = this.data?.mode === ADD_EDIT_DIALOG_CONSTANTS.MODES.ADD;
    if (isAdd) {
      this.submitAddClaim(formData);
    } else {
      this.submitEditClaim(formData);
    }
  }

  private prepareFormData(): any {
    const data = { ...this.form.value };
    delete data.officeCodeDisplay;
    delete data.opportunityOwnerName;
    delete data.mainResearchUserName;
    delete data.claimRenewalName;
    return data;
  }

  private submitAddClaim(formData: any): void {
    this.isSubmitting = true;
    this.submitError = null;
    const request = this.mapFormToCreateClaimRequest(formData);

    this.claimsService
      .createClaim(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.dialogRef.close({ action: 'add', data: formData, apiResponse: response });
          this.snackBarService.showSnackBar(
            CLAIMS_MICRO_UI.claimAddSuccessMessage,
            3000,
            CLASS_NAME.success,
          );
          this.reloadGridData();
        },
        error: () => {
          this.isSubmitting = false;
          this.snackBarService.showSnackBar(
            CLAIMS_MICRO_UI.claimAddErrorMessage,
            4000,
            CLASS_NAME.errors,
          );
        },
      });
  }

  private submitEditClaim(formData: any): void {
    if (!this.data?.claimData?.claimId) return;

    this.isSubmitting = true;
    this.submitError = null;
    const request = this.mapFormToCreateClaimRequest(formData).claims[0];

    this.claimsService
      .updateClaim(this.data.claimData.claimId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.dialogRef.close({ action: 'edit', data: request, apiResponse: response });
          const msg = CLAIMS_MICRO_UI.claimUpdateSuccessMessage;
          this.snackBarService.showSnackBar(msg, 3000, CLASS_NAME.success);
          this.reloadGridData();
        },
        error: () => {
          this.isSubmitting = false;
          const msg = CLAIMS_MICRO_UI.claimEditErrorMessage;
          this.snackBarService.showSnackBar(msg, 4000, CLASS_NAME.errors);
        },
      });
  }

  private reloadGridData(): void {
    document.dispatchEvent(new CustomEvent('claims.load-grid-data', {}));
  }

  private mapFormToCreateClaimRequest(data: any): ICreateClaimRequest {
    const d = data || {};
    return {
      claims: [
        {
          client: d.client ?? '',
          salesforceAccountId: d.sfAccountId ?? '',
          opportunityOwner: d.opportunityOwnerEmail ?? null,
          officeCode: d.officeCode ?? '',
          renewalClaimId: d.claimRenewalId ? Number(d.claimRenewalId) : null,
          claimExpiry: this.dataMapperService.formatDateForApi(d.expiryDate),
          mainResearchUserId: d.mainResearchUserId || null,
          claimWording: d.claimWording ?? '',
          claimFootnote: d.claimFootnote ?? '',
          counterSignatureDate: this.dataMapperService.formatDateForApi(d.counterSignDate),
          crmLink: d.crmLink ?? '',
          languageTranslationCode: d.languageTranslation ?? '',
        },
      ],
    };
  }

  public getIOfficeTooltip(): string {
    return this.selectedOffice
      ? `${UI_CONSTANTS.TOOLTIPS.OFFICE_CODE}: ${this.selectedOffice.offName} (${this.selectedOffice.offCode})`
      : '';
  }

  public getClientTooltip(): string {
    const clientValue = this.getFormControlValue('client');
    return clientValue
      ? `${UI_CONSTANTS.TOOLTIPS.CLIENT}: ${clientValue}`
      : UI_CONSTANTS.TOOLTIPS.CLIENT;
  }

  public getSfAccountTooltip(): string {
    const sfAccountValue = this.getFormControlValue('sfAccountId');
    return sfAccountValue
      ? `${UI_CONSTANTS.TOOLTIPS.SF_ACCOUNT}: ${sfAccountValue}`
      : UI_CONSTANTS.TOOLTIPS.SF_ACCOUNT;
  }

  public getOpportunityOwnerTooltip(): string {
    const opportunityOwnerValue = this.getFormControlValue('opportunityOwnerEmail');
    return opportunityOwnerValue
      ? `${UI_CONSTANTS.TOOLTIPS.OPPORTUNITY_OWNER}: ${opportunityOwnerValue}`
      : UI_CONSTANTS.TOOLTIPS.OPPORTUNITY_OWNER;
  }

  public getClaimRenewalTooltip(): string {
    const claimRenewalValue = this.getFormControlValue('claimRenewalId');
    if (!claimRenewalValue) return UI_CONSTANTS.TOOLTIPS.CLAIM_RENEWAL;
    const selected = this.claimRenewalSearchOptions.find((c) => c.id === claimRenewalValue);
    return selected
      ? `${UI_CONSTANTS.TOOLTIPS.CLAIM_RENEWAL}: ${selected.name}`
      : UI_CONSTANTS.TOOLTIPS.CLAIM_RENEWAL;
  }

  public getExpiryDateTooltip(): string {
    const expiryDateValue = this.getFormControlValue('expiryDate');
    return expiryDateValue
      ? `${UI_CONSTANTS.TOOLTIPS.EXPIRY_DATE}: ${this.dataMapperService.formatDateForDisplay(expiryDateValue)}`
      : UI_CONSTANTS.TOOLTIPS.EXPIRY_DATE;
  }

  public getResearchUserTooltip(): string {
    const researchUserValue = this.getFormControlValue('mainResearchUserId');
    if (!researchUserValue) return UI_CONSTANTS.TOOLTIPS.RESEARCH_USER;
    const selected = this.researchUserSearchOptions.find((u) => u.id === researchUserValue);
    return selected
      ? `${UI_CONSTANTS.TOOLTIPS.RESEARCH_USER}: ${selected.name}`
      : UI_CONSTANTS.TOOLTIPS.RESEARCH_USER;
  }

  public getClaimWordingTooltip(): string {
    const claimWordingValue = this.getFormControlValue('claimWording');
    return claimWordingValue
      ? `${UI_CONSTANTS.TOOLTIPS.CLAIM_WORDING}: ${claimWordingValue}`
      : UI_CONSTANTS.TOOLTIPS.CLAIM_WORDING;
  }

  public getClaimFootnoteTooltip(): string {
    const claimFootnoteValue = this.getFormControlValue('claimFootnote');
    return claimFootnoteValue
      ? `${UI_CONSTANTS.TOOLTIPS.CLAIM_FOOTNOTE}: ${claimFootnoteValue}`
      : UI_CONSTANTS.TOOLTIPS.CLAIM_FOOTNOTE;
  }

  public getCounterSignDateTooltip(): string {
    const counterSignDateValue = this.getFormControlValue('counterSignDate');
    return counterSignDateValue
      ? `${UI_CONSTANTS.TOOLTIPS.COUNTER_SIGN_DATE}: ${this.dataMapperService.formatDateForDisplay(counterSignDateValue)}`
      : UI_CONSTANTS.TOOLTIPS.COUNTER_SIGN_DATE;
  }

  public getCrmLinkTooltip(): string {
    const crmLinkValue = this.getFormControlValue('crmLink');
    return crmLinkValue
      ? `${UI_CONSTANTS.TOOLTIPS.CRM_LINK}: ${crmLinkValue}`
      : UI_CONSTANTS.TOOLTIPS.CRM_LINK;
  }

  public getLanguageTranslationTooltip(): string {
    const languageTranslationValue = this.getFormControlValue('languageTranslation');
    if (!languageTranslationValue) return UI_CONSTANTS.TOOLTIPS.LANGUAGE_TRANSLATION;
    const selected = this.languageOptions.find((l) => l.id === languageTranslationValue);
    return selected
      ? `${UI_CONSTANTS.TOOLTIPS.LANGUAGE_TRANSLATION}: ${selected.name}`
      : UI_CONSTANTS.TOOLTIPS.LANGUAGE_TRANSLATION;
  }

  private loadLanguageOptions(): void {
    this.isLoadingLanguages = true;
    this.claimsService
      .getLanguages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (languages) => {
          this.languageOptions = languages;
          this.languageSearchOptions = [...languages];
          this.isLoadingLanguages = false;
          this.prepopulateLanguageSelection();
        },
        error: () => {
          this.isLoadingLanguages = false;
          this.snackBarService.showSnackBar(
            CLAIMS_MICRO_UI.languageLoadingError,
            3000,
            CLASS_NAME.errors,
          );
        },
      });
  }

  private prepopulateLanguageSelection(): void {
    const currentValue = this.getFormControlValue('languageTranslation');
    if (!currentValue) {
      return;
    }

    const normalizedCurrentValue = String(currentValue).toLowerCase();
    const matchedLanguage = this.languageOptions.find(
      (language) => String(language.id).toLowerCase() === normalizedCurrentValue,
    );

    if (matchedLanguage) {
      this.setFormControlValue('languageTranslation', matchedLanguage.id);
      return;
    }
  }

  public onLanguageSearch(searchTerm: string): void {
    const term = (searchTerm || '').toLowerCase();
    this.languageSearchOptions = term.length === 0
      ? [...this.languageOptions]
      : this.languageOptions.filter((l) => l.name.toLowerCase().includes(term));
  }

  public onLanguageClick(): void {
    this.languageSearchOptions = [...this.languageOptions];
  }

  public onLanguageSelect(selection: SearchSelection): void {
    if (!selection) {
      this.setFormControlValue('languageTranslation', '');
      return;
    }
    this.setFormControlValue('languageTranslation', selection.id);
  }
}
